/**
 * datetime-local helpers: store local wall time in the UI, send UTC to APIs.
 * Portable — lives under traffic/ so web and mobile can share it.
 */

const pad2 = (num) => String(num).padStart(2, '0')

/**
 * Format a Date as `YYYY-MM-DDTHH:mm` for `<input type="datetime-local" />`.
 * Uses the local timezone (not UTC).
 *
 * @param {Date} [date]
 * @returns {string}
 */
export function getLocalDateTimeInputValue(date = new Date()) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

/**
 * Convert a datetime-local value (no timezone) to UTC ISO-8601 for APIs.
 * `YYYY-MM-DDTHH:mm` / `YYYY-MM-DDTHH:mm:ss` are interpreted as **local** wall time.
 * Values that already include `Z` or an offset are parsed as absolute instants.
 *
 * @param {string|null|undefined} localValue
 * @returns {string|null} e.g. `2026-07-22T20:00:00Z`
 */
export function localDateTimeInputToUtcIso(localValue) {
  if (localValue == null || typeof localValue !== 'string') {
    return null
  }
  const trimmed = localValue.trim()
  if (!trimmed) {
    return null
  }

  if (/[zZ]$/.test(trimmed) || /[+-]\d{2}:?\d{2}$/.test(trimmed)) {
    const ms = Date.parse(trimmed)
    if (Number.isNaN(ms)) {
      return null
    }
    return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z')
  }

  const match = trimmed.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/
  )
  if (!match) {
    const ms = Date.parse(trimmed)
    if (Number.isNaN(ms)) {
      return null
    }
    return new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z')
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hour = Number(match[4])
  const minute = Number(match[5])
  const second = match[6] != null ? Number(match[6]) : 0
  const date = new Date(year, month - 1, day, hour, minute, second, 0)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

/**
 * Convert local datetime-local to Valhalla `date_time.value` format:
 * `YYYY-MM-DDTHH:MM` using the UTC clock (no `Z`, no seconds).
 *
 * @param {string|null|undefined} localValue
 * @returns {string|null}
 */
export function localDateTimeInputToValhallaUtc(localValue) {
  const utcIso = localDateTimeInputToUtcIso(localValue)
  if (!utcIso) {
    return null
  }
  // e.g. 2026-07-22T20:00:00Z → 2026-07-22T20:00
  return utcIso.replace(/Z$/i, '').slice(0, 16)
}

/**
 * Build Valhalla / Drivepoints `date_time` for API calls.
 * UI keeps local datetime-local; value is UTC as `YYYY-MM-DDTHH:MM`.
 *
 * @param {{ type?: number, value?: string }|null|undefined} dateTime
 * @returns {{ type: number, value?: string }|null}
 */
export function toApiDateTime(dateTime) {
  if (!dateTime || dateTime.type == null || dateTime.type < 0) {
    return null
  }
  if (dateTime.type === 0) {
    return { type: 0 }
  }
  const valhallaValue = localDateTimeInputToValhallaUtc(dateTime.value)
  return {
    type: dateTime.type,
    value: valhallaValue || dateTime.value,
  }
}
